import { errorToast } from "@/v2-ui/toast"
import {
  type ConfirmButtonProps,
  ConfirmButton,
} from "@/v2-backoffice-client/components/button"
import { useUserStatusUpdateMutation } from "@/v2-backoffice-client/user/status/update/user.status.update.mutation"
import {
  type UserStatusUpdateCode,
  USER_STATUS_UPDATE_ACTIONS,
} from "@/v2-backoffice-client/user/status/update/user.status.update.constants"

export type UserStatusUpdateButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm"
> & {
  userId: number
  /** The status this button moves the user to. */
  statusCode: UserStatusUpdateCode
  onCompleted?(): void
}

// Moves one user to one status, the button counterpart to
// UserStatusUpdateActions (action menu). Callers render a button per status
// they want to offer and decide the layout; which statuses make sense for a
// given user is a placement question, not this button's.
function UserStatusUpdateButton(props: UserStatusUpdateButtonProps) {
  const { userId, statusCode, onCompleted, children, ...buttonProps } = props

  const [setStatus, { loading, error }] = useUserStatusUpdateMutation({
    onCompleted,
  })
  errorToast(error, { title: "Failed to update account status" })

  const action = USER_STATUS_UPDATE_ACTIONS[statusCode]

  return (
    <ConfirmButton
      variant={action.variant}
      iconSrc={action.iconSrc}
      content={action.content}
      confirmContent={action.confirmContent}
      confirmVariant={action.variant}
      isLoading={loading}
      onConfirm={() => {
        setStatus({ variables: { id: userId, userStatusCode: statusCode } })
      }}
      {...buttonProps}
    >
      {children ?? action.label}
    </ConfirmButton>
  )
}

export default UserStatusUpdateButton
